import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Calendar, 
  Sparkles, 
  BarChart3, 
  Image, 
  Clock,
  Users,
  Zap,
  ArrowRight,
  Check,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Menu,
  X
} from "lucide-react";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// StratIA Logo using actual image
const StratIALogo = ({ size = 32 }) => (
  <img 
    src="/stratia-logo.png" 
    alt="StratIA" 
    className="rounded-lg"
    style={{ width: size, height: size }}
  />
);

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Asistente IA",
      description: "Genera títulos, hashtags y contenido optimizado con inteligencia artificial"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Calendario Editorial",
      description: "Planifica y programa tus publicaciones en un calendario visual"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Analytics Avanzados",
      description: "Analiza el rendimiento de tus posts y descubre los mejores horarios"
    },
    {
      icon: <Image className="w-6 h-6" />,
      title: "Media Library",
      description: "Organiza tu banco de contenido multimedia en un solo lugar"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Puntos Calientes",
      description: "Descubre cuándo tu audiencia está más activa"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Publicación Multi-Red",
      description: "Publica en todas tus redes desde una sola plataforma"
    }
  ];

  const platforms = [
    { icon: <Twitter className="w-8 h-8" />, name: "Twitter" },
    { icon: <Instagram className="w-8 h-8" />, name: "Instagram" },
    { icon: <Facebook className="w-8 h-8" />, name: "Facebook" },
    { icon: <Linkedin className="w-8 h-8" />, name: "LinkedIn" },
    { icon: <TikTokIcon className="w-8 h-8" />, name: "TikTok" },
    { icon: <Youtube className="w-8 h-8" />, name: "YouTube" },
  ];

  const testimonials = [
    {
      name: "María García",
      role: "Content Creator",
      avatar: "https://images.unsplash.com/photo-1618587068859-d9903ef41bb2?w=100&h=100&fit=crop",
      quote: "StratIA ha transformado mi forma de gestionar redes. Ahorro horas cada semana."
    },
    {
      name: "Carlos Ruiz",
      role: "Social Media Manager",
      avatar: "https://images.unsplash.com/photo-1617925357736-8a4ea869800?w=100&h=100&fit=crop",
      quote: "La IA para hashtags es increíble. Mi engagement ha crecido un 40%."
    }
  ];

  return (
    <div className="min-h-screen bg-background neural-grid">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <StratIALogo size={36} />
              <span className="font-bold text-xl tracking-tight gradient-neural-text">StratIA</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-primary transition-colors">Funciones</a>
              <a href="#platforms" className="text-muted-foreground hover:text-primary transition-colors">Plataformas</a>
              <a href="#testimonials" className="text-muted-foreground hover:text-primary transition-colors">Testimonios</a>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="login-btn">Iniciar Sesión</Button>
              </Link>
              <Link to="/register">
                <Button className="gradient-neural glow-neural text-white" data-testid="register-btn">Comenzar Gratis</Button>
              </Link>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-card border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-muted-foreground hover:text-primary">Funciones</a>
              <a href="#platforms" className="block text-muted-foreground hover:text-primary">Plataformas</a>
              <a href="#testimonials" className="block text-muted-foreground hover:text-primary">Testimonios</a>
              <div className="pt-4 border-t border-border space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Iniciar Sesión</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full gradient-neural">Comenzar Gratis</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-2 rounded-full mb-6 glow-cyan">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Potenciado por IA</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Gestiona tus redes con{" "}
                <span className="gradient-neural-text">inteligencia</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                La plataforma todo-en-uno para creadores de contenido. Planifica, crea y analiza tu presencia en redes sociales con la ayuda de IA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="gradient-neural glow-neural text-white" data-testid="hero-cta">
                    Comenzar Gratis
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="border-primary/50 hover:bg-primary/10">Ver Funciones</Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-primary" />
                  <span>7 días gratis</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-primary" />
                  <span>Sin tarjeta requerida</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl glow-neural">
                <img 
                  src="https://images.pexels.com/photos/4152607/pexels-photo-4152607.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Content creator working"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"></div>
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-card/90 backdrop-blur-md p-4 rounded-xl shadow-lg border border-primary/30 glow-cyan">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 gradient-neural rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold gradient-neural-text">+40%</p>
                    <p className="text-sm text-muted-foreground">Engagement</p>
                  </div>
                </div>
              </div>
              {/* Floating AI Card */}
              <div className="absolute -top-4 -right-4 bg-card/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-accent/30 glow-purple">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium">IA Activa</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Todo lo que necesitas para <span className="gradient-neural-text">triunfar</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Herramientas poderosas diseñadas para creadores de contenido y managers de redes sociales
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 bg-card rounded-xl border border-border hover:border-primary/50 transition-all hover:glow-cyan"
              >
                <div className="w-12 h-12 gradient-neural rounded-xl flex items-center justify-center mb-4 group-hover:glow-neural transition-all">
                  <span className="text-white">{feature.icon}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section id="platforms" className="py-20 px-4 sm:px-6 lg:px-8 bg-card/50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Conecta todas tus <span className="gradient-neural-text">redes</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
            Gestiona todas tus plataformas desde un solo lugar
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {platforms.map((platform, index) => (
              <div 
                key={index}
                className="group flex flex-col items-center gap-3 p-6 bg-card rounded-xl border border-border hover:border-primary/50 transition-all hover:glow-cyan"
              >
                <div className="text-muted-foreground group-hover:text-primary transition-colors">
                  {platform.icon}
                </div>
                <span className="text-sm font-medium">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Lo que dicen nuestros <span className="gradient-neural-text">usuarios</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="p-6 bg-card rounded-xl border border-border hover:border-primary/30 transition-all"
              >
                <p className="text-lg mb-6 italic text-muted-foreground">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-primary/30"
                  />
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-12 rounded-2xl overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 gradient-neural opacity-10" />
            <div className="absolute inset-0 bg-card/80 backdrop-blur-sm" />
            <div className="absolute inset-0 border border-primary/30 rounded-2xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Empieza a crear contenido <span className="gradient-neural-text">inteligente</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                Únete a miles de creadores que ya están usando StratIA para potenciar su presencia en redes sociales
              </p>
              <Link to="/register">
                <Button size="lg" className="gradient-neural glow-neural text-white">
                  Comenzar Gratis
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <StratIALogo size={28} />
              <span className="font-bold gradient-neural-text">StratIA</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/terms" className="hover:text-primary transition-colors">
                Términos de Servicio
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors">
                Política de Privacidad
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 StratIA. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
